import { useEffect, useState } from 'react'
import { debounce, throttle, type DebouncedFunc } from 'lodash-es'
import type { TOCItem } from './TableOfContent'

type TableOfContentNavParams = {
  items: TOCItem[]
  richTextArticle: HTMLElement
  setActiveItemId: (id: string) => void
}

class TableOfContentNav {
  articleBody: HTMLElement
  headers: NodeListOf<HTMLHeadingElement>
  items: TOCItem[]
  setActiveItemId: (id: string) => void
  debouncedUpdateState: DebouncedFunc<() => void>
  throttledUpdateState: DebouncedFunc<() => void>
  viewPortHeight = 0
  offsetY = 0
  articlePartsPos: {
    top: number
    bottom: number
    itemId: string
  }[] = []

  constructor({ items, richTextArticle, setActiveItemId }: TableOfContentNavParams) {
    this.articleBody = richTextArticle
    this.headers = this.articleBody.querySelectorAll(':scope > h2, h3')
    this.items = items
    this.setActiveItemId = setActiveItemId

    this.debouncedUpdateState = debounce(this.updateState, 300)
    this.throttledUpdateState = throttle(this.updateState, 1000)

    window.addEventListener('scroll', this.checkScrollPosition)
    /* Recalculation of sizes after loading lazy components with throttling */
    window.addEventListener('scroll', this.throttledUpdateState)
    window.addEventListener('resize', this.debouncedUpdateState)
    this.updateState()
  }

  setSizes = () => {
    this.viewPortHeight = window.innerHeight
    this.offsetY = this.viewPortHeight / 2

    this.articlePartsPos = []

    this.items.forEach((item, i) => {
      const nextIndex = i + 1
      const bottomPos =
        nextIndex !== this.items.length
          ? this.headers[nextIndex].getBoundingClientRect().top + window.scrollY
          : this.articleBody.getBoundingClientRect().bottom + window.scrollY

      this.articlePartsPos.push({
        top: this.headers[i].getBoundingClientRect().top + window.scrollY,
        bottom: bottomPos,
        itemId: item.id,
      })
    })
  }

  checkScrollPosition = () => {
    const scrollTop = window.scrollY + this.offsetY

    this.articlePartsPos.some((articlePart) => {
      if (scrollTop >= articlePart.top && scrollTop < articlePart.bottom) {
        this.setActiveItemId(articlePart.itemId)
        return true
      }
      this.setActiveItemId('')
      return false
    })
  }

  updateState = () => {
    this.setSizes()
    this.checkScrollPosition()
  }

  destroy = () => {
    window.removeEventListener('scroll', this.checkScrollPosition)
    window.removeEventListener('scroll', this.throttledUpdateState)
    window.removeEventListener('resize', this.debouncedUpdateState)
  }
}

export const useTableOfContent = ({
  items,
  richTextArticle,
}: {
  items: TOCItem[]
  richTextArticle: HTMLElement | null
}) => {
  const [activeItemId, setActiveItemId] = useState('')

  useEffect(() => {
    if (!richTextArticle) return

    const tocNav = new TableOfContentNav({ items, richTextArticle, setActiveItemId })

    return tocNav.destroy
  }, [items, richTextArticle])

  return activeItemId
}
